import { Badge } from '@/components/ui/Badge/badge';
import { SessionUser } from '@certhon/models';
import { formatDistanceToNow } from 'date-fns';

type TagAuthState = 'enabled' | 'requested' | 'disabled';

function getTagAuthState(user: SessionUser): TagAuthState {
  if (user.tag_id) return 'enabled';
  if (user.requested_tag_id) return 'requested';
  return 'disabled';
}

const STATE_CONFIG: Record<
  TagAuthState,
  { label: string; variant: 'positive' | 'default' | 'secondary' }
> = {
  enabled: { label: 'Enabled', variant: 'positive' },
  requested: { label: 'Requested', variant: 'default' },
  disabled: { label: 'Disabled', variant: 'secondary' },
};

/**
 * Read-only tag authentication status for the signed-in user. Requesting a
 * tag (NFC scan + PIN) and changing the PIN are not available here yet.
 */
export function TagAuthStatus({ user }: { user: SessionUser }) {
  const state = getTagAuthState(user);
  const { label, variant } = STATE_CONFIG[state];

  return (
    <div className="flex items-start justify-between gap-4">
      <div className="grid gap-1">
        <span className="text-sm font-medium">Tag authentication</span>
        <span className="text-muted-foreground text-xs">
          {state === 'enabled' &&
            'You can sign in with your door tag and PIN on supported devices.'}
          {state === 'requested' &&
            `Your request${
              user.requested_tag_on
                ? ` from ${formatDistanceToNow(new Date(user.requested_tag_on), { addSuffix: true })}`
                : ''
            } is awaiting approval by an account administrator.`}
          {state === 'disabled' &&
            'Tag authentication is not set up for your account. Use the current app to request it.'}
        </span>
      </div>
      <Badge variant={variant}>{label}</Badge>
    </div>
  );
}
